import React, { createRef, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { FilterParams, InitialStorageFilters } from './Filters';
import {
  ApiBestPricePackageModel,
  ApiGeoUnit,
  ApiGetFlightTripPackageRequestFromJSON,
  ApiItemType,
  ApiPostalCode,
  ApiPriceFromJSON,
  ApiPromotionCodeFromJSON
} from '@ibe/api';
import {
  PackageSearchParams,
  Price,
  RentalCarOfficeWithDistanceWrapper,
  useApi,
  useAppService,
  useConfig
} from '@ibe/components';
import Keys from '../../Translations/generated/product-ibe.de.json.keys';
import { ConfigModel, itemListEvent } from '@ibe/services';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { TFunction } from 'react-i18next';
import { isValidPromotionCode, PromotionCodeData } from '../../Components/PromotionCode';
import {
  BUS_SESSION_STORAGE_KEYS,
  BUS_STOPS_PERIMETER,
  getReducedBusStationList,
  packageFilterSessionStorage,
  SELF_ARRIVAL_CODE
} from '../Util/utils';
import useThgConfig from '../../Hooks/useThgConfig';
import { Tooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { PackageTypes } from './ProductIBE';
import { PackageCode } from './PackagesList';
import {
  MINI_GROUP,
  MINI_GROUP_IMG,
  NORMAL_GROUP,
  SMALL_GROUP,
  SMALL_GROUP_IMG
} from '../../Util/constant';
import type { ListRow, ListRowItem } from 'Components/PackageSimpleList/types';

dayjs.extend(localeData);

export const productIBEAnchor = 'iso-product-ibe-list';

const AVAILABILITY_LIMIT = 10;

const getAvailability = (availability?: number): { value: string; class: string } => {
  if (!!availability && availability > AVAILABILITY_LIMIT) {
    return { value: `> ${AVAILABILITY_LIMIT}`, class: 'simple-list__cell__availability--level2' };
  } else if (!!availability && availability === AVAILABILITY_LIMIT) {
    return { value: `${availability}`, class: 'simple-list__cell__availability--level2' };
  } else if (!!availability && availability <= AVAILABILITY_LIMIT) {
    return { value: `${availability}`, class: 'simple-list__cell__availability--level1' };
  } else {
    return { value: `< ${AVAILABILITY_LIMIT}`, class: 'simple-list__cell__availability--level1' };
  }
};

const usePackagesListFunctions = (
  earliestPossibleDate: string,
  latestPossibleDate: string,
  packageCodes: Array<PackageCode>,
  t: TFunction,
  promotionCode: PromotionCodeData,
  initialStorageFilters: InitialStorageFilters,
  numberOfTravelers?: number,
  maintenanceMode?: boolean,
  packageType?: string,
  suppressStrikePrices?: boolean
) => {
  const api = useApi();
  const appService = useAppService();
  const config = useConfig();
  const thgConfig = useThgConfig();
  const imgBaseUrl = thgConfig.baseUrl || config.translationsUrl;

  const [possibleOrigins, setPossibleOrigins] = useState<string[]>([]);
  const [packages, setPackages] = useState<ApiBestPricePackageModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialRequestFinished, setInitialRequestFinished] = useState<boolean>(false);
  const promotionCodeRef = useRef<PromotionCodeData>(promotionCode);
  const [initialPackageSearchParams, setInitialPackageSearchParams] = useState<
    PackageSearchParams | undefined
  >(undefined);

  const [hasSelfArrival, setHasSelfArrival] = useState<boolean>(false);
  const [onlySelfArrival, setOnlySelfArrival] = useState<boolean>(false);

  useEffect(() => {
    if (packageType !== PackageTypes.PBUS || !packageCodes || packageCodes.length === 0) return;

    const checkSelfArrival = async () => {
      try {
        const response = await api.searchFlightTripPackages(
          ApiGetFlightTripPackageRequestFromJSON({
            busStations: [],
            startDate: getEarliestPossibleDate(earliestPossibleDate, config),
            endDate: latestPossibleDate,
            roomContainer: [
              {
                adults: numberOfTravelers ?? 2,
                children: 0,
                infants: 0,
                childrenAges: [],
                infantsAges: []
              }
            ],
            origins: [],
            packageCodes: packageCodes.map(pkgCode => ({
              packageCode: pkgCode.code,
              groupSize: pkgCode.groupSize
            })),
            subType: ApiItemType.PACKAGE
          })
        );

        const allCodes = new Set(
          (response.packages ?? []).flatMap((p: ApiBestPricePackageModel) => p.stationCodes ?? [])
        );

        const hasSelf = allCodes.has(SELF_ARRIVAL_CODE);
        const onlySelf = hasSelf && allCodes.size === 1;

        setHasSelfArrival(hasSelf);
        setOnlySelfArrival(onlySelf);
      } catch (err) {
        console.error('Error checking self arrival:', err);
      }
    };

    checkSelfArrival();
  }, [packageType, packageCodes]);

  const getPackages = useCallback(
    async (filters?: FilterParams): Promise<void> => {
      if (maintenanceMode || !packageCodes || (!!packageCodes && packageCodes.length === 0)) {
        return;
      }
      setIsLoading(true);
      let hasPackages = false;
      const getBusStations = (filterParams?: FilterParams) => {
        if (packageType !== PackageTypes.PBUS) return null;
        if (onlySelfArrival || filterParams?.busStations?.includes(SELF_ARRIVAL_CODE)) return [];
        return filterParams?.busStations ?? [];
      };
      try {
        const response = await api.searchFlightTripPackages(
          ApiGetFlightTripPackageRequestFromJSON({
            busStations: getBusStations(filters),
            startDate: filters?.startDate ?? getEarliestPossibleDate(earliestPossibleDate, config),
            endDate: filters?.endDate ?? latestPossibleDate,
            latestPossibleDate: latestPossibleDate,
            roomContainer: [
              {
                adults: filters?.numberOfTravelers ?? numberOfTravelers ?? 2,
                children: 0,
                infants: 0,
                childrenAges: [],
                infantsAges: []
              }
            ],
            origins: filters?.origins,
            packageCodes: packageCodes
              .filter(code => {
                if (!filters?.groupSize) return true;
                const activeSizes = filters.groupSize.split(',');
                return activeSizes.includes(code.groupSize);
              })
              .map(pkgCode => ({
                packageCode: pkgCode.code,
                groupSize: pkgCode.groupSize
              })),
            subType: ApiItemType.PACKAGE,
            promotionCode: isValidPromotionCode(promotionCode.promotionCodeData)
              ? ApiPromotionCodeFromJSON({
                  code: promotionCode.promotionCodeData.code,
                  percentageValue: promotionCode.promotionCodeData.percentageValue,
                  absoluteValue: promotionCode.promotionCodeData.absoluteValue
                })
              : undefined
          })
        );
        if (response?.packages) {
          const bestPricePackages = response.packages;
          let filteredPackages = bestPricePackages;

          if (packageType === PackageTypes.PBUS) {
            filteredPackages = filterBusPackages(bestPricePackages);
          }

          setPackages(filteredPackages);

          itemListEvent.broadcast({ items: filteredPackages });

          if (
            filteredPackages.some(
              (singlePackage: ApiBestPricePackageModel) => !!singlePackage.origin
            )
          ) {
            const cachedFilter = packageFilterSessionStorage.get();
            const fetchedPossibleOrigins = filteredPackages
              .filter((singlePackage: ApiBestPricePackageModel) => !!singlePackage.origin)
              .map((singlePackage: ApiBestPricePackageModel) => singlePackage.origin);
            const finalOrigins = [
              ...new Set([
                ...(cachedFilter?.possibleOrigins ?? []),
                ...(fetchedPossibleOrigins ?? [])
              ])
            ];
            setPossibleOrigins(finalOrigins);
          }

          if (!initialPackageSearchParams) {
            setInitialPackageSearchParams({
              startDate:
                filters?.startDate ?? getEarliestPossibleDate(earliestPossibleDate, config),
              endDate: filters?.endDate ?? latestPossibleDate,
              occupancy: [
                {
                  adults: filters?.numberOfTravelers ?? numberOfTravelers ?? 2,
                  children: 0,
                  infants: 0,
                  childrenAges: [],
                  infantsAges: []
                }
              ],
              origins: filters?.origins.flatMap(origin => ({ id: origin } as ApiGeoUnit))
            });
          }

          hasPackages = true;
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
        if (hasPackages) {
          setTimeout(() => {
            const { hash } = location;
            if (!!hash) {
              const anchor = document.getElementById(hash.substring(1));
              if (!!anchor) {
                anchor.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }, 300);
        }
      }
    },
    [api, promotionCode, packageCodes, maintenanceMode, numberOfTravelers, packageType]
  );

  const filterBusPackages = (
    busPackages: ApiBestPricePackageModel[]
  ): ApiBestPricePackageModel[] => {
    const selectedStations = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.selectedStations);

    if (!selectedStations) return [];

    const parsedSelectedStations: RentalCarOfficeWithDistanceWrapper[] = JSON.parse(
      selectedStations
    );

    if (parsedSelectedStations.some(station => station.code === SELF_ARRIVAL_CODE)) {
      return busPackages.filter(busPackage => busPackage.stationCodes?.includes(SELF_ARRIVAL_CODE));
    }

    const selectedStationCodes = new Set(parsedSelectedStations.map(station => station.code));

    return busPackages.filter(busPackage =>
      busPackage.stationCodes?.some(stationCode => selectedStationCodes.has(stationCode))
    );
  };
  const hasPromotionCodeChanged = (val: PromotionCodeData): boolean => {
    return JSON.stringify(val) !== JSON.stringify(promotionCodeRef.current);
  };
  useEffect(() => {
    (async () => {
      if (
        !!packageCodes &&
        promotionCode.initialPromotionCodeChecked &&
        initialStorageFilters.initialFiltersChecked &&
        (!initialRequestFinished || hasPromotionCodeChanged(promotionCode)) &&
        packageType &&
        (packageType !== PackageTypes.PBUS ||
          (packageType === PackageTypes.PBUS &&
            initialStorageFilters?.initialFilters?.ownArrivalsAndDepartures))
      ) {
        if (!!initialStorageFilters.initialFilters) {
          await getPackages({ ...initialStorageFilters.initialFilters });
        } else {
          await getPackages();
        }
        setInitialRequestFinished(true);
        promotionCodeRef.current = promotionCode;
      }
    })();
  }, [
    earliestPossibleDate,
    latestPossibleDate,
    packageCodes,
    promotionCode,
    maintenanceMode,
    initialRequestFinished,
    initialStorageFilters,
    packageType,
    onlySelfArrival
  ]);

  const isSelfArrivalOption = (): boolean => {
    const currentPostCode = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.selectedPostCode);
    if (currentPostCode) {
      try {
        const parsedPostCode: ApiPostalCode = JSON.parse(currentPostCode);
        if (parsedPostCode.code === SELF_ARRIVAL_CODE) return true;
      } catch (err) {
        console.error(err);
      }
    }
    const currentStations = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.selectedStations);
    if (currentStations) {
      try {
        const parsedStations: RentalCarOfficeWithDistanceWrapper[] = JSON.parse(currentStations);
        return parsedStations.some(station => station.code === SELF_ARRIVAL_CODE);
      } catch (err) {
        console.error(err);
        return false;
      }
    }
    return false;
  };

  const countBusStops = (
    busStations: string[] | undefined,
    possibleBusStations: RentalCarOfficeWithDistanceWrapper[]
  ) => {
    if (isSelfArrivalOption() && busStations?.includes(SELF_ARRIVAL_CODE)) {
      return 1;
    }
    const reducedList = getReducedBusStationList(busStations);
    if (possibleBusStations) {
      return possibleBusStations.filter(
        busStationData =>
          reducedList.includes(busStationData.code ? busStationData.code : 'dummy') &&
          busStationData.distance <= BUS_STOPS_PERIMETER
      ).length;
    }
    return 0;
  };

  const collectBusStopsInfo = (
    busStops: string[] | undefined,
    possibleBusStations: RentalCarOfficeWithDistanceWrapper[]
  ): React.ReactNode => {
    if (isSelfArrivalOption() && busStops?.includes(SELF_ARRIVAL_CODE)) {
      return t(Keys.busStops);
    }
    const reducedList = getReducedBusStationList(busStops);
    if (possibleBusStations) {
      return possibleBusStations
        .filter(
          busStationData =>
            reducedList.includes(busStationData.code ? busStationData.code : 'dummy') &&
            busStationData.distance <= BUS_STOPS_PERIMETER
        )
        .map(actualBusStop => (
          <li key={actualBusStop.code}>
            <FontAwesomeIcon icon={faCheck} className={'bus_tooltip_icon'} />
            {actualBusStop.description + ' (' + Math.round(actualBusStop.distance) + ' km)'}
          </li>
        ));
    }
    return <></>;
  };

  const sanitizeString = (id: string) => {
    return id.replaceAll(/[= &]/g, '');
  };

  const [tooltipOpen, setTooltipOpen] = useState<Map<string, boolean>>(new Map());
  const toggle = (id: string) => {
    setTooltipOpen(new Map(tooltipOpen.set(id, tooltipOpen.has(id) ? !tooltipOpen.get(id) : true)));
  };

  const isTooltipOpen = (packageId: string) => {
    return tooltipOpen.has(packageId) ? tooltipOpen.get(packageId) : false;
  };

  const getSelectedStations = (): RentalCarOfficeWithDistanceWrapper[] => {
    const selectedStations = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.selectedStations);
    if (!selectedStations) return [];
    return JSON.parse(selectedStations);
  };

  const calculateSupplementPrice = (singlePackage: ApiBestPricePackageModel): number => {
    const supplements = singlePackage.stationSupplements ?? [];

    const selectedStations = getSelectedStations();
    if (!selectedStations || !singlePackage.stationCodes) return 0;

    const currentShownStations = selectedStations.filter(
      busStationData =>
        singlePackage.stationCodes?.includes(busStationData.code ?? 'dummy') &&
        busStationData.distance <= BUS_STOPS_PERIMETER
    );

    const selectedCodes = new Set(currentShownStations.map(s => s.code ?? 'dummy'));

    // Map stationCode -> supplement price (undefined if missing)
    const pricesMap = new Map<string, number | undefined>();
    selectedCodes.forEach(code => {
      const supplement = supplements.find(sup => sup.stationCode === code);
      pricesMap.set(code, supplement?.surcharge?.finalPrice);
    });

    const prices = Array.from(pricesMap.values()).filter((p): p is number => p !== undefined);

    const hasNoSupplementStation = Array.from(pricesMap.values()).some(p => p === undefined);

    const negativeSupplements = prices.filter(p => p < 0);
    const positiveSupplements = prices.filter(p => p > 0);

    if (negativeSupplements.length) {
      return Math.min(...negativeSupplements);
    }

    if (positiveSupplements.length && !hasNoSupplementStation) {
      return Math.min(...positiveSupplements);
    }

    return 0;
  };

  const packagesMappingCallback = useCallback(
    (singlePackage: ApiBestPricePackageModel): ListRow<ListRowItem> => {
      const possibleBusStations = getSelectedStations();
      const busStopCount = countBusStops(singlePackage.stationCodes, possibleBusStations);
      const tooltipTextContent = collectBusStopsInfo(
        singlePackage.stationCodes,
        possibleBusStations
      );

      const packageId = sanitizeString(singlePackage.id);
      const getGroupSizeItem = (groupSize?: string): ListRowItem => {
        if (groupSize === SMALL_GROUP) {
          return {
            smallGroup: {
              value: '',
              imgSrc: `${imgBaseUrl}${SMALL_GROUP_IMG}`,
              hideInMobile: false,
              className: `simple-list__cell__groupSize ${
                groupSize === SMALL_GROUP ? '' : 'no-groupSize'
              }`
            }
          };
        } else if (groupSize === MINI_GROUP) {
          return {
            miniGroup: {
              value: '',
              imgSrc: `${imgBaseUrl}${MINI_GROUP_IMG}`,
              hideInMobile: false,
              className: `simple-list__cell__groupSize ${
                groupSize === MINI_GROUP ? '' : 'no-groupSize'
              }`
            }
          };
        } else if (
          groupSize === NORMAL_GROUP &&
          packages.some(innerSinglePackage => innerSinglePackage.groupSize !== NORMAL_GROUP)
        ) {
          return { empty: { value: '' } };
        } else {
          return {};
        }
      };

      return {
        id: singlePackage.id,
        code: singlePackage.code,
        specialRow: singlePackage.groupSize !== NORMAL_GROUP,
        groupSize: singlePackage.groupSize ?? '',
        nodeRef: createRef<HTMLElement | undefined>() as RefObject<HTMLElement | undefined>,
        item: {
          startDateImg: {
            value: '',
            imgSrc: `${imgBaseUrl}/img/calendar.svg`,
            hideInDesktop: true,
            className: 'simple-list__cell__startDate--img'
          },
          startDate: {
            value: !!singlePackage.startDate
              ? `${
                  dayjs().localeData().weekdaysShort()[dayjs(singlePackage.startDate).day()]
                } ${dayjs(singlePackage.startDate).format(
                  config.displayFormatDate[appService.lang.replace('-', '_')]
                )}`
              : '',
            className: 'simple-list__cell__startDate'
          },
          ...getGroupSizeItem(singlePackage.groupSize),
          originImg: {
            value: '',
            imgSrc:
              singlePackage.typeCode === PackageTypes.PFLUG
                ? `${imgBaseUrl}/img/airplane.svg`
                : `${imgBaseUrl}/img/bus.svg`,
            hideInDesktop: true,
            className: 'simple-list__cell__origin--img'
          },
          origin: {
            value:
              singlePackage.typeCode === 'PFLUG' ? (
                singlePackage.originName ?? ''
              ) : (
                <div id={packageId + '_cont'} className={'simple-list__cell__bus-stops'}>
                  <div
                    id={packageId + '_tooltip'}
                    onClick={() => toggle(singlePackage.id)}
                    role="button"
                    onKeyDown={() => toggle(singlePackage.id)}
                  >
                    {busStopCount} {busStopCount > 1 ? t(Keys.busStop_plural) : t(Keys.busStop)}
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className={'simple-list__cell__bus-stops-icon'}
                    ></FontAwesomeIcon>
                  </div>
                  <Tooltip
                    placement={'bottom'}
                    isOpen={isTooltipOpen(singlePackage.id)}
                    target={packageId + '_tooltip'}
                    toggle={() => toggle(singlePackage.id)}
                    popperClassName="tooltip-popper"
                    innerClassName="tooltip-inner"
                    container={packageId + '_cont'}
                    onClick={() => toggle(singlePackage.id)}
                    autohide={true}
                  >
                    <div className="tooltip__text">
                      {t(Keys.possibleBusStops, { count: busStopCount })}
                    </div>
                    <div className="tooltip__info">{tooltipTextContent}</div>
                  </Tooltip>
                </div>
              ),
            className: 'simple-list__cell__origin'
          },
          availabilityImg: {
            value: '',
            imgSrc: `${imgBaseUrl}/img/seat.svg`,
            hideInDesktop: true,
            className: 'simple-list__cell__availability--img'
          },
          availability: {
            value: getAvailability(singlePackage.availability).value,
            className: `simple-list__cell__availability ${
              getAvailability(singlePackage.availability).class
            }`
          },
          price: {
            value: (
              <>
                <div
                  className={
                    singlePackage.price?.basePrice
                      ? 'simple-list__cell__price__reduced'
                      : 'simple-list__cell__price__normal'
                  }
                >
                  {t(Keys.from)}{' '}
                  {singlePackage.price?.basePrice ? (
                    <Price
                      price={ApiPriceFromJSON({
                        finalPrice:
                          singlePackage.price.basePrice + calculateSupplementPrice(singlePackage),
                        currencyCode: singlePackage.price.currencyCode,
                        modifiers: []
                      })}
                      currencyDisplay="symbol"
                      removeZeroDecimals
                      displayInline
                      currencyFirst={true}
                      useGrouping={false}
                    />
                  ) : (
                    <Price
                      price={ApiPriceFromJSON({
                        finalPrice:
                          singlePackage.price.finalPrice + calculateSupplementPrice(singlePackage),
                        currencyCode: singlePackage.price.currencyCode,
                        modifiers: []
                      })}
                      currencyDisplay="symbol"
                      removeZeroDecimals
                      displayInline
                      currencyFirst={true}
                      useGrouping={false}
                    />
                  )}
                </div>{' '}
                {!!singlePackage.price?.basePrice && (
                  <div className={'simple-list__cell__price__striked'}>
                    {t(Keys.instead)}{' '}
                    {React.createElement(
                      suppressStrikePrices ? React.Fragment : 's',
                      null,
                      <Price
                        price={ApiPriceFromJSON({
                          finalPrice: singlePackage.price.finalPrice,
                          currencyCode: singlePackage.price.currencyCode,
                          modifiers: []
                        })}
                        currencyDisplay="symbol"
                        removeZeroDecimals
                        displayInline
                        currencyFirst={true}
                        useGrouping={false}
                      />
                    )}
                  </div>
                )}
              </>
            ),
            className: 'simple-list__cell__price'
          }
        }
      };
    },
    [packages, tooltipOpen]
  );

  return {
    packages,
    isLoading,
    possibleOrigins,
    getPackages,
    packagesMappingCallback,
    initialPackageSearchParams,
    hasSelfArrival,
    onlySelfArrival
  };
};

export default usePackagesListFunctions;

export const getEarliestPossibleDate = (date: string, config: ConfigModel) => {
  return dayjs(date).isBefore(dayjs(new Date()))
    ? dayjs(
        `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`
      ).format(config.formatDate)
    : date;
};
